import type { FC } from 'react';
import { useState, useEffect, useCallback } from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FacebookIcon from '@mui/icons-material/Facebook';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useRouter } from 'src/hooks/use-router';
import { notificationsApi, leadsApi, subscribeToLeads, subscribeToNotifications, type NotificationData, notificationTypeColors } from 'src/api/supabase';
import { getBookings, isCalcomConfigured, type CalcomBooking } from 'src/api/calcom';
import { getLatestRecordingForCustomer } from 'src/api/atlas';
import { formatDistanceToNow } from 'date-fns';
import type { Customer } from 'src/types/customer';

// LocalStorage key for tracking seen Cal.com bookings
const SEEN_CALCOM_BOOKINGS_KEY = 'seen_calcom_booking_ids';

export const NotificationsButton: FC = () => {
  const router = useRouter();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [facebookLeads, setFacebookLeads] = useState<Customer[]>([]);
  const [facebookLeadsCount, setFacebookLeadsCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const open = Boolean(anchorEl);

  // Fetch notifications, unread count, and Facebook leads
  const fetchNotifications = useCallback(async () => {
    try {
      const [response, count, fbLeads, fbCount] = await Promise.all([
        notificationsApi.getAll({ pageSize: 10 }),
        notificationsApi.getUnreadCount(),
        leadsApi.getFacebookLeads(2),
        leadsApi.getFacebookLeadsCount(),
      ]);
      setNotifications(response.notifications);
      setUnreadCount(count);
      setFacebookLeads(fbLeads);
      setFacebookLeadsCount(fbCount);
    } catch (error) {
      // console.error('Error fetching notifications:', error);
    }
  }, []);

  // Check for new Cal.com bookings and create notifications + leads
  const checkCalcomBookings = useCallback(async () => {
    if (!isCalcomConfigured()) return;

    try {
      console.log('[Cal.com → Lead] Checking for new bookings...');
      const bookings = await getBookings({ status: 'upcoming' });
      console.log('[Cal.com → Lead] Found bookings:', bookings.length);

      // Get seen booking IDs from localStorage
      const seenIds: string[] = JSON.parse(localStorage.getItem(SEEN_CALCOM_BOOKINGS_KEY) || '[]');
      console.log('[Cal.com → Lead] Previously seen IDs:', seenIds.length);

      // Find new bookings
      const newBookings = bookings.filter((b) => !seenIds.includes(String(b.id)));
      console.log('[Cal.com → Lead] New bookings to process:', newBookings.length);

      // Track successfully processed booking IDs
      const successfullyProcessedIds: string[] = [];

      // Create notifications AND leads for new bookings
      for (const booking of newBookings) {
        console.log('[Cal.com → Lead] Processing booking:', booking.id, booking.title);

        const attendee = booking.attendees?.[0];
        console.log('[Cal.com → Lead] Attendee:', attendee);

        const customerName = attendee?.name || 'Unknown Customer';
        const customerEmail = attendee?.email || '';
        const scheduledDate = new Date(booking.startTime);
        const location = booking.location || undefined;

        // Extract phone from booking metadata or description
        let customerPhone: string | undefined;

        // Try to get phone from metadata (check various possible locations)
        if (booking.metadata?.phone) {
          customerPhone = String(booking.metadata.phone);
        } else if (booking.metadata?.phoneNumber) {
          customerPhone = String(booking.metadata.phoneNumber);
        } else if (booking.metadata?.responses?.phone) {
          customerPhone = String(booking.metadata.responses.phone);
        } else if ((booking.metadata?.responses as any)?.['phone-number']) {
          customerPhone = String((booking.metadata?.responses as any)['phone-number']);
        }

        console.log('[Cal.com → Lead] Extracted phone from metadata:', customerPhone);
        console.log('[Cal.com → Lead] Full metadata:', JSON.stringify(booking.metadata));

        // Try to extract phone from description if not in metadata
        if (!customerPhone && booking.description) {
          const phoneMatch = booking.description.match(/(?:phone|tel|mobile|cell)?:?\s*(\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/i);
          if (phoneMatch) {
            customerPhone = phoneMatch[1].replace(/[^\d+]/g, '');
            console.log('[Cal.com → Lead] Extracted phone from description:', customerPhone);
          }
        }

        // Parse address from location field
        let customerAddress: string | undefined;
        let customerCity: string | undefined;
        let customerState: string | undefined;
        let customerZip: string | undefined;

        if (location && !location.includes('http') && !location.includes('@')) {
          // Location seems to be an address, try to parse it
          customerAddress = location;

          // Try to extract city, state, zip from address string
          // Common formats: "123 Main St, Seattle, WA 98101" or "123 Main St, Seattle WA 98101"
          const addressParts = location.split(',').map((p) => p.trim());
          if (addressParts.length >= 2) {
            customerAddress = addressParts[0];
            const cityStateZip = addressParts.slice(1).join(', ');

            // Try to extract state and zip
            const stateZipMatch = cityStateZip.match(/([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?/);
            if (stateZipMatch) {
              customerState = stateZipMatch[1];
              customerZip = stateZipMatch[2];
              customerCity = cityStateZip.replace(stateZipMatch[0], '').replace(/,\s*$/, '').trim();
            } else {
              customerCity = addressParts[1];
            }
          }
        }

        // Create notification
        try {
          await notificationsApi.createScheduledQuoteNotification(customerName, scheduledDate, location);
          console.log('[Cal.com → Lead] Created notification for:', customerName);
        } catch (notifError) {
          console.error('[Cal.com → Lead] Failed to create notification:', notifError);
        }

        // Create lead in Supabase - always try, even without attendee
        try {
          // Parse name into first/last
          const nameParts = customerName.trim().split(/\s+/);
          const firstName = nameParts[0] || 'Unknown';
          const lastName = nameParts.slice(1).join(' ') || '';

          console.log('[Cal.com → Lead] Creating/updating lead for:', firstName, lastName, customerEmail);

          // Check if lead already exists by email or phone
          let existingLead: Customer | null = null;
          if (customerEmail) {
            const { data: existingLeads } = await leadsApi.getAll({
              filters: { query: customerEmail },
              rowsPerPage: 1,
            });
            if (existingLeads && existingLeads.length > 0) {
              existingLead = existingLeads[0];
              console.log('[Cal.com → Lead] Found existing lead by email:', existingLead.id);
            }
          }

          // Also try to find by phone if no email match
          if (!existingLead && customerPhone) {
            const { data: existingLeads } = await leadsApi.getAll({
              filters: { query: customerPhone },
              rowsPerPage: 1,
            });
            if (existingLeads && existingLeads.length > 0) {
              existingLead = existingLeads[0];
              console.log('[Cal.com → Lead] Found existing lead by phone:', existingLead.id);
            }
          }

          let createdOrUpdatedLead: Customer | null = null;

          if (!existingLead) {
            // Create new lead with all available info
            console.log('[Cal.com → Lead] Creating NEW lead...');
            createdOrUpdatedLead = await leadsApi.create({
              firstName,
              lastName,
              email: customerEmail || undefined,
              phone: customerPhone,
              address1: customerAddress,
              city: customerCity,
              state: customerState || 'WA',
              zipCode: customerZip,
              leadStatus: 'scheduled_quote',
              leadSource: 'website' as any, // Cal.com booking
              projectNotes: [
                `Booked via Cal.com: ${booking.title || 'In-Person Quote'}`,
                `Scheduled: ${scheduledDate.toLocaleString()}`,
                location && !customerAddress ? `Location: ${location}` : '',
                booking.description ? `Notes: ${booking.description}` : '',
              ].filter(Boolean).join('\n'),
              estimatedInstallDate: scheduledDate.toISOString().split('T')[0],
              inPersonQuoteDate: scheduledDate.toISOString().split('T')[0],
              inPersonQuoteTime: scheduledDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
            });
            console.log('[Cal.com → Lead] Created new lead:', createdOrUpdatedLead?.id);
          } else {
            // Update existing lead with any new info
            console.log('[Cal.com → Lead] Updating existing lead:', existingLead.id);
            const updateData: Partial<Customer> = {
              leadStatus: 'scheduled_quote',
              estimatedInstallDate: scheduledDate.toISOString().split('T')[0],
              inPersonQuoteDate: scheduledDate.toISOString().split('T')[0],
              inPersonQuoteTime: scheduledDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
            };

            // Only update fields that are empty in existing lead
            if (!existingLead.phone && customerPhone) updateData.phone = customerPhone;
            if (!existingLead.address1 && customerAddress) updateData.address1 = customerAddress;
            if (!existingLead.city && customerCity) updateData.city = customerCity;
            if (!existingLead.state && customerState) updateData.state = customerState;
            if (!existingLead.zipCode && customerZip) updateData.zipCode = customerZip;

            // Append to project notes
            updateData.projectNotes = existingLead.projectNotes
              ? `${existingLead.projectNotes}\n\n---\nCal.com booking: ${scheduledDate.toLocaleString()}${location ? ` at ${location}` : ''}`
              : `Cal.com booking: ${scheduledDate.toLocaleString()}${location ? ` at ${location}` : ''}`;

            createdOrUpdatedLead = await leadsApi.update(existingLead.id, updateData);
            console.log('[Cal.com → Lead] Updated existing lead:', createdOrUpdatedLead?.id);
          }

          // Mark this booking as successfully processed
          successfullyProcessedIds.push(String(booking.id));

          // Try to link Atlas call recording to this customer
          if (createdOrUpdatedLead && (createdOrUpdatedLead.phone || customerPhone)) {
            try {
              const atlasRecording = await getLatestRecordingForCustomer({
                phone: createdOrUpdatedLead.phone || customerPhone,
                name: customerName,
                firstName,
                lastName,
              });

              if (atlasRecording?.recordingUrl) {
                // Add recording info to project notes
                const recordingNote = `\n\n---\nAtlas Discovery Call Recording: ${atlasRecording.recordingUrl}`;
                await leadsApi.update(createdOrUpdatedLead.id, {
                  projectNotes: (createdOrUpdatedLead.projectNotes || '') + recordingNote,
                });
                console.log('[Cal.com → Lead] Linked Atlas recording to lead');
              }
            } catch (atlasError) {
              console.log('[Cal.com → Lead] Atlas lookup failed (non-critical):', atlasError);
            }
          }
        } catch (leadError) {
          // Log the error but don't add to seen IDs so we can retry
          console.error('[Cal.com → Lead] Failed to create/update lead:', leadError);
        }
      }

      // Only update seen IDs for successfully processed bookings
      if (successfullyProcessedIds.length > 0) {
        const newSeenIds = [...new Set([...seenIds, ...successfullyProcessedIds])].slice(-100);
        localStorage.setItem(SEEN_CALCOM_BOOKINGS_KEY, JSON.stringify(newSeenIds));
        console.log('[Cal.com → Lead] Updated seen IDs, added:', successfullyProcessedIds.length);
      }
    } catch (error) {
      console.error('[Cal.com → Lead] Error checking Cal.com bookings:', error);
    }
  }, []);

  // Initial fetch and polling every 60 seconds (as backup to real-time)
  useEffect(() => {
    fetchNotifications();
    checkCalcomBookings();
    const interval = setInterval(() => {
      fetchNotifications();
      checkCalcomBookings();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications, checkCalcomBookings]);

  // Real-time subscription for new leads
  useEffect(() => {
    const unsubscribeLeads = subscribeToLeads((payload) => {
      if (payload.eventType === 'INSERT' && payload.new) {
        const newLead = payload.new;
        // If it's a Facebook lead, add to the Facebook leads list
        if (newLead.leadStatus === 'facebook_new') {
          setFacebookLeads((prev) => {
            if (prev.find((l) => l.id === newLead.id)) return prev;
            return [newLead, ...prev].slice(0, 5); // Keep max 5
          });
          setFacebookLeadsCount((prev) => prev + 1);
        }
      } else if (payload.eventType === 'UPDATE' && payload.new) {
        const updatedLead = payload.new;
        // If lead status changed from facebook_new, remove from list
        if (updatedLead.leadStatus !== 'facebook_new') {
          setFacebookLeads((prev) => {
            const filtered = prev.filter((l) => l.id !== updatedLead.id);
            if (filtered.length !== prev.length) {
              setFacebookLeadsCount((c) => Math.max(0, c - 1));
            }
            return filtered;
          });
        }
      }
    });

    const unsubscribeNotifications = subscribeToNotifications((payload) => {
      if (payload.eventType === 'INSERT' && payload.new) {
        // New notification - add to top of list
        const newNotification: NotificationData = {
          id: payload.new.id,
          createdAt: payload.new.created_at,
          updatedAt: payload.new.updated_at,
          title: payload.new.title,
          message: payload.new.message,
          type: payload.new.type,
          read: payload.new.read,
          readAt: payload.new.read_at || undefined,
          link: payload.new.link || undefined,
          metadata: payload.new.metadata || {},
        };
        setNotifications((prev) => [newNotification, ...prev].slice(0, 10));
        if (!newNotification.read) {
          setUnreadCount((prev) => prev + 1);
        }
      }
    });

    return () => {
      unsubscribeLeads();
      unsubscribeNotifications();
    };
  }, []);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: NotificationData) => {
    // Mark as read
    if (!notification.read) {
      await notificationsApi.markAsRead(notification.id);
      setUnreadCount((prev) => Math.max(0, prev - 1));
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
      );
    }

    // Navigate if there's a link
    if (notification.link) {
      router.push(notification.link);
    }

    handleClose();
  };

  const handleFacebookLeadClick = (lead: Customer) => {
    router.push(`/dashboard/customers/${lead.id}`);
    handleClose();
  };

  const handleMarkAllRead = async () => {
    await notificationsApi.markAllAsRead();
    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  // Total badge count: unread notifications + facebook leads count
  const totalBadgeCount = facebookLeadsCount;

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpen}
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          <Badge
            badgeContent={totalBadgeCount > 0 ? totalBadgeCount : undefined}
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                fontWeight: 700,
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                right: -4,
                top: -4,
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)',
              },
            }}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <SvgIcon sx={{ fontSize: 22, color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(30, 41, 59, 0.85)' }}>
              <NotificationsIcon />
            </SvgIcon>
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 520,
            bgcolor: isDarkMode ? '#111111' : '#ffffff',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            borderRadius: '12px',
            boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.15)',
            mt: 1,
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5 }}
        >
          <Typography sx={{ fontWeight: 600, color: isDarkMode ? '#fff' : '#1e293b' }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Typography
              component="button"
              onClick={handleMarkAllRead}
              sx={{
                fontSize: '0.75rem',
                color: '#2970FF',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Mark all as read
            </Typography>
          )}
        </Stack>

        <Divider sx={{ borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' }} />

        {/* Facebook Leads Section */}
        {facebookLeads.length > 0 && (
          <>
            <Box sx={{ px: 2, py: 1.5, bgcolor: alpha('#1877F2', 0.08) }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FacebookIcon sx={{ fontSize: 18, color: '#1877F2' }} />
                <Typography sx={{ fontWeight: 600, color: '#1877F2', fontSize: '0.8rem' }}>
                  New Facebook Leads ({facebookLeadsCount})
                </Typography>
              </Stack>
            </Box>
            <List disablePadding>
              {facebookLeads.map((lead) => (
                <ListItemButton
                  key={lead.id}
                  onClick={() => handleFacebookLeadClick(lead)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                    bgcolor: alpha('#1877F2', 0.05),
                    '&:hover': {
                      bgcolor: alpha('#1877F2', 0.1),
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: '100%' }}>
                    {/* Facebook icon */}
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: alpha('#1877F2', 0.15),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <FacebookIcon sx={{ fontSize: 18, color: '#1877F2' }} />
                    </Box>
                    <ListItemText
                      primary={`${lead.firstName} ${lead.lastName}`.trim() || 'Unknown'}
                      secondary={
                        <Stack spacing={0.25}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.7)',
                              fontSize: '0.8rem',
                            }}
                          >
                            {lead.phone || lead.email || 'No contact info'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(30, 41, 59, 0.4)' }}
                          >
                            {lead.createdAt
                              ? formatDistanceToNow(new Date(lead.createdAt), { addSuffix: true })
                              : 'Just now'}
                          </Typography>
                        </Stack>
                      }
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: 600,
                          color: isDarkMode ? '#fff' : '#1e293b',
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  </Stack>
                </ListItemButton>
              ))}
            </List>
            <Divider sx={{ borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' }} />
          </>
        )}

        {/* Regular Notifications List */}
        {notifications.length === 0 && facebookLeads.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(30, 41, 59, 0.5)' }}>
              No notifications yet
            </Typography>
          </Box>
        ) : notifications.length > 0 ? (
          <List disablePadding sx={{ maxHeight: 280, overflow: 'auto' }}>
            {notifications.map((notification) => (
              <ListItemButton
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                  bgcolor: notification.read ? 'transparent' : (isDarkMode ? 'rgba(41, 112, 255, 0.08)' : 'rgba(41, 112, 255, 0.06)'),
                  '&:hover': {
                    bgcolor: notification.read
                      ? (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)')
                      : (isDarkMode ? 'rgba(41, 112, 255, 0.12)' : 'rgba(41, 112, 255, 0.1)'),
                  },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: '100%' }}>
                  {/* Type indicator */}
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: notificationTypeColors[notification.type],
                      mt: 0.75,
                      flexShrink: 0,
                    }}
                  />
                  <ListItemText
                    primary={notification.title}
                    secondary={
                      <Stack spacing={0.5}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.7)',
                            fontSize: '0.8125rem',
                            lineHeight: 1.4,
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(30, 41, 59, 0.4)' }}
                        >
                          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                        </Typography>
                      </Stack>
                    }
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: notification.read ? 500 : 600,
                        color: isDarkMode ? '#fff' : '#1e293b',
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Stack>
              </ListItemButton>
            ))}
          </List>
        ) : null}
      </Popover>
    </>
  );
};
